import React, { useEffect, useRef, useState } from "react";

import { homeService } from "api/services/marketing.service";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import { BANNER_IMAGE_ALLOWED_EXTS, BANNER_IMAGE_MAX_BYTES, ITEM_FORM_FIELDS, formatTourFormValue, getIdSelectOptions, getIdSelectSourceItem, getTourYearOptions } from "utils/constant/home";

import type { HomeScreenSettingComponent, HomeScreenSettingItem, HomeScreenSettingItemPayload } from "api/types/marketing.types";
import type { IdSelectSourcesData } from "utils/constant/home";
import type { HomeComponentType } from "utils/type/home";

interface HomeScreenItemModalProps {
  open: boolean;
  selectedComponent: HomeScreenSettingComponent | null;
  editingItem: HomeScreenSettingItem | null;
  idSelectSources: IdSelectSourcesData;
  idSelectLoading: boolean;
  toursLoading?: boolean;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payload: HomeScreenSettingItemPayload) => void | Promise<void>;
  onTourMonthChange?: (month: string) => void;
}

/** 연도 + 1~12월 → YYYY-MM 값 생성 */
const buildMonthValue = (year: number, monthNum: number): string => `${year}-${String(monthNum).padStart(2, "0")}`;

/** YYYY-MM(또는 YYYY-MM-DD) 문자열에서 연도/월 추출. 연도가 다르면 월 버튼도 선택 해제되어야 하므로 둘 다 본다. */
const parseYearMonth = (value: string): { year: number; month: number } | null => {
  if (!value) return null;
  const [y, m] = value.split("-");
  const year = Number(y);
  const month = Number(m);
  if (!Number.isFinite(year) || !Number.isFinite(month)) return null;
  if (month < 1 || month > 12) return null;
  return { year, month };
};

/** HomeScreenSettingItem → 폼 입력값(map<string,string>)으로 변환 */
const itemToFormValues = (item: HomeScreenSettingItem, type: HomeComponentType): Record<string, string> => {
  const fields = ITEM_FORM_FIELDS[type] ?? [];
  const form: Record<string, string> = {};
  const itemRecord = item as unknown as Record<string, unknown>;
  fields.forEach((field) => {
    // tour idSelect: dropdown value 가 합성 키 (`d:{departureId}` / `t:{tourId}`) 이므로 별도 복원.
    if (field.type === "idSelect" && field.source === "tours") {
      form[field.key] = formatTourFormValue(item);
      return;
    }
    // month: 백엔드에 저장되지 않는 UI 전용 필드. start_date 에서 YYYY-MM 복원.
    if (field.type === "month") {
      const startDate = itemRecord.start_date;
      form[field.key] = typeof startDate === "string" && startDate.length >= 7 ? startDate.slice(0, 7) : "";
      return;
    }
    const raw = itemRecord[field.key];
    if (raw == null) {
      form[field.key] = "";
      return;
    }
    if (field.type === "tags" && Array.isArray(raw)) {
      form[field.key] = raw.join(", ");
      return;
    }
    form[field.key] = String(raw);
  });
  return form;
};

const HomeScreenItemModal: React.FC<HomeScreenItemModalProps> = ({ open, selectedComponent, editingItem, idSelectSources, idSelectLoading, toursLoading = false, submitting, onClose, onSubmit, onTourMonthChange }) => {
  const [itemForm, setItemForm] = useState<Record<string, string>>({});
  // 월 그리드가 어느 연도를 가리키는지. 월 선택 전에도 연도는 골라둘 수 있어야 해서 별도 state.
  const [monthYear, setMonthYear] = useState<number>(() => new Date().getFullYear());
  const [imageUploading, setImageUploading] = useState<Record<string, boolean>>({});
  const imageInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const isEditing = editingItem !== null;
  const fields = selectedComponent ? (ITEM_FORM_FIELDS[selectedComponent.component_type] ?? []) : [];
  const hasMonthField = fields.some((f) => f.type === "month");
  const tourMonthValue = itemForm.tour_month ?? "";

  useEffect(() => {
    if (!open) return;
    if (editingItem && selectedComponent) {
      const form = itemToFormValues(editingItem, selectedComponent.component_type);
      setItemForm(form);
      // 저장된 값의 연도로 그리드를 맞춘다 (내년 투어를 수정할 때 올해로 열리면 안 됨)
      setMonthYear(parseYearMonth(form.tour_month ?? "")?.year ?? new Date().getFullYear());
    } else {
      setItemForm({});
      setMonthYear(new Date().getFullYear());
    }
    setImageUploading({});
  }, [open, editingItem, selectedComponent]);

  const handleImageFileChange = async (key: string, e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    // BE 사양과 동일한 사전 검증: 확장자, 크기 (불필요한 네트워크 호출 방지)
    const ext = (file.name.split(".").pop() ?? "").toLowerCase();
    if (!BANNER_IMAGE_ALLOWED_EXTS.includes(ext)) {
      alert(`지원하지 않는 이미지 형식입니다. (${BANNER_IMAGE_ALLOWED_EXTS.join(", ")} 만 가능)`);
      return;
    }
    if (file.size > BANNER_IMAGE_MAX_BYTES) {
      alert("이미지 크기는 30MB를 초과할 수 없습니다.");
      return;
    }

    setImageUploading((prev) => ({ ...prev, [key]: true }));
    try {
      const res = await homeService.uploadBannerImage(file);
      const url = res.data?.url;
      if (!url) throw new Error("upload response missing url");
      setItemForm((prev) => ({ ...prev, [key]: url }));
    } catch (err) {
      const beMessage = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      alert(beMessage ?? "이미지 업로드에 실패했습니다.");
    } finally {
      setImageUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  // tour_month 가 바뀌면 부모에게 알려 해당 월의 투어 목록을 다시 불러오게 한다
  useEffect(() => {
    if (!open) return;
    if (!hasMonthField) return;
    if (!tourMonthValue) return;
    onTourMonthChange?.(tourMonthValue);
  }, [open, hasMonthField, tourMonthValue, onTourMonthChange]);

  const buildPayload = (): HomeScreenSettingItemPayload | null => {
    if (!selectedComponent) return null;
    const baseOrder = editingItem?.order ?? Math.max(-1, ...selectedComponent.items.map((i) => i.order)) + 1;
    const baseActive = editingItem?.is_active ?? true;

    let payload: Partial<HomeScreenSettingItemPayload> = {
      order: baseOrder,
      is_active: baseActive,
    };

    // 1차: idSelect autofill 먼저 적용
    for (const field of fields) {
      if (field.type !== "idSelect" || !field.source) continue;
      const raw = itemForm[field.key];
      if (!raw) {
        if (field.required) return null;
        continue;
      }
      const autofilled = getIdSelectSourceItem(field.source, raw, idSelectSources);
      if (autofilled) payload = { ...payload, ...autofilled };
    }

    // 2차: 일반 필드는 autofill 을 덮어쓸 수 있도록 뒤에 적용
    for (const field of fields) {
      if (field.type === "idSelect") continue;
      const raw = itemForm[field.key];
      if (raw == null || raw === "") {
        if (field.required) return null;
        continue;
      }
      // month: 투어 필터링용 UI 필드라 서버 페이로드에는 싣지 않는다
      if (field.type === "month") continue;

      if (field.type === "number") {
        const n = Number(raw);
        if (!Number.isNaN(n)) {
          (payload as Record<string, unknown>)[field.key] = n;
        }
        continue;
      }
      if (field.type === "tags") {
        const tags = raw
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean);
        (payload as Record<string, unknown>)[field.key] = tags;
        continue;
      }
      (payload as Record<string, unknown>)[field.key] = raw;
    }

    if (fields.some((f) => f.key === "title")) {
      const finalTitle = (payload.title ?? "").trim();
      if (!finalTitle) return null;
      payload.title = finalTitle;
    }

    return payload as HomeScreenSettingItemPayload;
  };

  const handleSubmit = () => {
    if (submitting) return;
    const payload = buildPayload();
    if (!payload) {
      alert("필수 항목을 입력해주세요.");
      return;
    }
    onSubmit(payload);
  };

  return (
    <Modal
      open={open}
      title={isEditing ? "항목 수정" : "항목 추가"}
      onClose={onClose}
      closeOnOverlayClick={false}
      width="480px"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? "저장 중..." : isEditing ? "저장" : "추가"}
          </Button>
        </>
      }
    >
      {selectedComponent && (
        <div className="home-screen__item-form">
          {fields.map((field) => {
            const key = field.key;
            const value = itemForm[key] ?? "";
            const onChange = (v: string) => setItemForm((prev) => ({ ...prev, [key]: v }));

            if (field.type === "month") {
              const parsed = parseYearMonth(value);
              // 그리드가 보고 있는 연도와 선택된 값의 연도가 같을 때만 월 버튼을 활성 표시
              const selectedMonth = parsed && parsed.year === monthYear ? parsed.month : null;
              const handleYearChange = (nextYear: number) => {
                setMonthYear(nextYear);
                // 이미 월이 선택돼 있으면 연도만 바꿔 그대로 재조회, 없으면 월 선택 대기
                if (parsed) onChange(buildMonthValue(nextYear, parsed.month));
              };
              return (
                <div key={key} className="home-screen__add-field">
                  <label className="home-screen__add-label">{field.label + (field.required ? " *" : "")}</label>
                  <Select options={getTourYearOptions()} value={String(monthYear)} onChange={(e) => handleYearChange(Number(e.target.value))} className="home-screen__month-year-select" />
                  <div className="home-screen__month-grid">
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                      const selected = selectedMonth === m;
                      return (
                        <button key={m} type="button" className={`home-screen__month-btn ${selected ? "home-screen__month-btn--selected" : ""}`} onClick={() => onChange(buildMonthValue(monthYear, m))}>
                          {m}월
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            }

            if (field.type === "idSelect" && field.source) {
              const isTours = field.source === "tours";
              const needsMonth = isTours && hasMonthField && !tourMonthValue;
              const loading = isTours ? toursLoading : idSelectLoading;
              let placeholder = "선택";
              if (loading) placeholder = "불러오는 중...";
              else if (needsMonth) placeholder = "월을 먼저 선택해주세요";
              const opts = [{ value: "", label: placeholder }, ...(needsMonth ? [] : getIdSelectOptions(field.source, idSelectSources))];
              return <Select key={key} label={field.label + (field.required ? " *" : "")} options={opts} value={value} onChange={(e) => onChange(e.target.value)} disabled={loading || needsMonth} />;
            }

            if (field.type === "select" && field.options) {
              const opts = [{ value: "", label: "선택" }, ...field.options];
              return <Select key={key} label={field.label + (field.required ? " *" : "")} options={opts} value={value} onChange={(e) => onChange(e.target.value)} />;
            }

            if (field.type === "datetime") {
              return <Input key={key} label={field.label + (field.required ? " *" : "")} type="datetime-local" value={value} onChange={(e) => onChange(e.target.value)} />;
            }

            if (field.type === "tags") {
              return <Input key={key} label={field.label + (field.required ? " *" : "")} placeholder={field.placeholder ?? "쉼표로 구분"} value={value} onChange={(e) => onChange(e.target.value)} />;
            }

            if (field.type === "image") {
              const uploading = !!imageUploading[key];
              return (
                <div key={key} className="home-screen__add-field home-screen__image-field">
                  <label className="home-screen__add-label">{field.label + (field.required ? " *" : "")}</label>
                  <div className="home-screen__image-input-row">
                    <Input type="text" placeholder={field.placeholder ?? "https://..."} value={value} onChange={(e) => onChange(e.target.value)} className="home-screen__image-url-input" />
                    <input
                      ref={(el) => {
                        imageInputRefs.current[key] = el;
                      }}
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleImageFileChange(key, e)}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => imageInputRefs.current[key]?.click()} disabled={uploading} style={{ height: "34px" }}>
                      {uploading ? "업로드 중..." : "파일 업로드"}
                    </Button>
                  </div>
                  {field.hint && <p className="home-screen__image-hint">{field.hint}</p>}
                  {/* 이모지 입력(퀵메뉴 아이콘 등)은 깨진 이미지로 보이므로 URL 형태일 때만 미리보기 */}
                  {value && /^(https?:\/\/|\/)/.test(value) && (
                    <div className="home-screen__image-preview">
                      <img src={value} alt="preview" />
                    </div>
                  )}
                </div>
              );
            }

            return <Input key={key} label={field.label + (field.required ? " *" : "")} type={field.type === "number" ? "number" : "text"} placeholder={field.placeholder} value={value} onChange={(e) => onChange(e.target.value)} />;
          })}
        </div>
      )}
    </Modal>
  );
};

export default HomeScreenItemModal;
